/** Client-only. Returns false during SSR to avoid hydration mismatches. */
export const isVoiceRecordingSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function' &&
  typeof MediaRecorder !== 'undefined'

const SILENCE_MS = 5_000
const SILENCE_THRESHOLD = 0.015
const WAVEFORM_BARS = 48
const WAVEFORM_GAIN = 4.5
const WAVEFORM_FLOOR = 0.08

export type VoiceRecordingOptions = {
  onLevelChange?: (levels: number[]) => void
  onSilence?: () => void
}

export const createVoiceRecording = (
  options: VoiceRecordingOptions = {}
): {
  start: () => Promise<void>
  stop: () => Promise<File>
} => {
  if (!isVoiceRecordingSupported()) {
    throw new Error(
      'Voice input is not supported in this browser. Please use a supported browser or type your message instead.'
    )
  }

  let mediaRecorder: MediaRecorder | null = null
  let stream: MediaStream | null = null
  let audioContext: AudioContext | null = null
  let analyser: AnalyserNode | null = null
  let animationFrameId: number | null = null
  let silenceStartedAt: number | null = null
  let silenceTriggered = false
  const chunks: BlobPart[] = []

  const cleanupAnalyser = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    if (audioContext) {
      audioContext.close().catch(() => {})
      audioContext = null
    }

    analyser = null
    silenceStartedAt = null
  }

  const monitorLevels = () => {
    if (!analyser) return

    const data = new Uint8Array(analyser.fftSize)
    analyser.getByteTimeDomainData(data)

    let sumSquares = 0
    for (let i = 0; i < data.length; i++) {
      const normalized = (data[i] - 128) / 128
      sumSquares += normalized * normalized
    }
    const rms = Math.sqrt(sumSquares / data.length)

    const levels = Array.from({ length: WAVEFORM_BARS }, (_, index) => {
      const start = Math.floor((index / WAVEFORM_BARS) * data.length)
      const end = Math.max(
        start + 1,
        Math.floor(((index + 1) / WAVEFORM_BARS) * data.length)
      )
      let peak = 0

      for (let i = start; i < end; i++) {
        peak = Math.max(peak, Math.abs((data[i] - 128) / 128))
      }

      // Boost quiet speech so bars react more visibly; keep a small floor for dots.
      const boosted = Math.pow(Math.min(1, peak * WAVEFORM_GAIN), 0.7)
      return Math.max(WAVEFORM_FLOOR, boosted)
    })

    options.onLevelChange?.(levels)

    if (rms < SILENCE_THRESHOLD) {
      if (silenceStartedAt === null) {
        silenceStartedAt = Date.now()
      } else if (
        !silenceTriggered &&
        Date.now() - silenceStartedAt >= SILENCE_MS
      ) {
        silenceTriggered = true
        options.onSilence?.()
      }
    } else {
      silenceStartedAt = null
    }

    animationFrameId = requestAnimationFrame(monitorLevels)
  }

  const start = async () => {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true })

    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      mediaRecorder = new MediaRecorder(stream, { mimeType })
      chunks.length = 0
      silenceTriggered = false
      silenceStartedAt = null

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data)
      }

      audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      monitorLevels()

      mediaRecorder.start(250)
    } catch (error) {
      cleanupAnalyser()
      stream.getTracks().forEach((track) => track.stop())
      stream = null
      mediaRecorder = null
      throw error
    }
  }

  const stop = () =>
    new Promise<File>((resolve, reject) => {
      if (!mediaRecorder) {
        reject(new Error('Recording has not started'))
        return
      }

      mediaRecorder.onstop = () => {
        cleanupAnalyser()
        stream?.getTracks().forEach((track) => track.stop())

        if (chunks.length === 0) {
          reject(new Error('No audio was recorded'))
          return
        }

        const mimeType = mediaRecorder.mimeType || 'audio/webm'
        const contentType = mimeType.split(';')[0]
        const blob = new Blob(chunks, { type: contentType })

        resolve(
          new File([blob], `chat-ai-voice-${Date.now()}.webm`, {
            type: contentType
          })
        )
      }

      mediaRecorder.onerror = () => {
        cleanupAnalyser()
        stream?.getTracks().forEach((track) => track.stop())
        reject(new Error('Recording failed'))
      }

      try {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.requestData()
        }
        mediaRecorder.stop()
      } catch (error) {
        cleanupAnalyser()
        stream?.getTracks().forEach((track) => track.stop())
        stream = null
        mediaRecorder = null
        reject(
          error instanceof Error ? error : new Error('Recording failed to stop')
        )
      }
    })

  return { start, stop }
}

type PlaybackListener = (playingMessageId: string | null) => void

let currentAudio: HTMLAudioElement | null = null
let currentMessageId: string | null = null
const playbackListeners = new Set<PlaybackListener>()

const notifyPlaybackListeners = () => {
  playbackListeners.forEach((listener) => listener(currentMessageId))
}

export const subscribeToChatAiPlayback = (
  listener: PlaybackListener
): (() => void) => {
  playbackListeners.add(listener)
  listener(currentMessageId)

  return () => {
    playbackListeners.delete(listener)
  }
}

export const stopChatAiPlayback = () => {
  if (!currentAudio) {
    currentMessageId = null
    notifyPlaybackListeners()
    return
  }

  currentAudio.pause()
  currentAudio.currentTime = 0
  currentAudio = null
  currentMessageId = null
  notifyPlaybackListeners()
}

export const playChatAiAudio = (
  messageId: string,
  audio: HTMLAudioElement
): Promise<void> => {
  if (currentMessageId === messageId && currentAudio === audio) {
    stopChatAiPlayback()
    return Promise.resolve()
  }

  stopChatAiPlayback()

  currentAudio = audio
  currentMessageId = messageId
  notifyPlaybackListeners()

  const handleEnded = () => {
    if (currentAudio !== audio) return
    currentAudio = null
    currentMessageId = null
    notifyPlaybackListeners()
  }

  audio.onended = handleEnded

  return audio.play().catch((error) => {
    if (currentAudio === audio) {
      currentAudio = null
      currentMessageId = null
      notifyPlaybackListeners()
    }
    throw error
  })
}
